import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../../hooks/seo-referencement';
import Button from '../../components/ui/button';
import DrawerBooking from '../../components/ui/drawer-booking';
import ServiceCard from '../../components/client/service-card';
import OptionsSelector from '../../components/client/options-selector';
import CollaboratorSelector from '../../components/client/collaborator-selector';
import ClientForm from '../../components/client/client-form';
import TimeSlotSelector from '../../components/client/time-slot-selector';
import ReservationConditions from '../../components/client/reservation-conditions';
import PromoCodeInput from '../../components/client/promo-code-input';
import ProfessionalInfoCard from '../../components/client/professional-info-card';
import SalonPhotos from '../../components/client/salon-photos';
import ProfessionalStats from '../../components/client/professional-stats';
import ProfessionalDescription from '../../components/client/professional-description';
import { BookingSkeleton } from '../../components/client/booking-skeleton';
import MultiServiceCart from '../../components/client/multi-service-cart';
import { bookingClientViewModel } from '../../view-model/client/client-booking-view-model';
import Header from '../../components/navbar/Header';
import Footer from '../../components/footer/footer';


/**
 * Page de réservation client
 * Affiche les services du professionnel et permet de réserver un créneau
 */
const BookingClient: React.FC = observer(() => {
  const { businessName } = useParams<{ businessName: string }>();
  const { t } = useTranslation();
  
  // État pour stocker les informations du professionnel pour le SEO
  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    image: undefined as string | undefined,
  });

  useSEO(seoData);

  // Initialiser le ViewModel avec l'ID du professionnel
  useEffect(() => {
    if (businessName) {
      bookingClientViewModel.initialize(businessName);
      
      // Mettre à jour les données SEO une fois que les informations du professionnel sont chargées
      const updateSeoData = () => {
        // Utiliser les propriétés individuelles du view-model
        const displayName = bookingClientViewModel.businessName || '';
        const description = bookingClientViewModel.description || '';
        const logoUrl = bookingClientViewModel.logoUrl;
        const city = bookingClientViewModel.city || '';
        const services = bookingClientViewModel.services || [];

        // Construire un titre SEO optimisé
        const seoTitle = `Prestaslot, prends ta prestation avec ${displayName}`;

        // Construire une description SEO optimisée
        const servicesText = services.length > 0
          ? services.slice(0, 3).map(s => s.name).join(', ')
          : t('booking.bookServices');
        const seoDescription = description ||
          `${t('booking.bookServices')} ${displayName}${city ? ` à ${city}` : ''}. ${servicesText}. ${t('booking.availableOnline')}`;

        setSeoData({
          title: seoTitle,
          description: seoDescription,
          image: logoUrl || undefined,
        });
      };
      
      // Vérifier si les données sont déjà chargées ou attendre qu'elles le soient
      if (!bookingClientViewModel.isLoading && bookingClientViewModel.businessName) {
        updateSeoData();
      } else {
        const checkInterval = setInterval(() => {
          if (!bookingClientViewModel.isLoading && bookingClientViewModel.businessName) {
            updateSeoData();
            clearInterval(checkInterval);
          }
        }, 500);
        
        // Nettoyer l'intervalle
        return () => clearInterval(checkInterval);
      }
    }
  }, [businessName, t]);
  
  // Gérer la soumission du formulaire
  const handleSubmit = async () => {
    if (!bookingClientViewModel.isFormValid) {
      return;
    }

    // Le ViewModel gère la création de la réservation et la redirection vers Stripe
    await bookingClientViewModel.createBooking();
    bookingClientViewModel.toggleDrawer(false);
  };
  
  // Afficher un skeleton loader pendant le chargement ou en cas d'erreur
  // Cela améliore le SEO en affichant du contenu structuré pour les robots
  if ((bookingClientViewModel.isLoading && bookingClientViewModel.services.length === 0) ||
      (bookingClientViewModel.error && bookingClientViewModel.services.length === 0)) {
    return <BookingSkeleton />;
  }
  
  return (
    <>
    <Header />
    <div className="min-h-screen bg-white/80 backdrop-blur-sm relative overflow-hidden pb-16 pt-24 md:pt-20 md:pb-8">
      
      
      <div className="relative z-10">
        
        {/* Informations du professionnel et statistiques des avis */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 w-full">
          {/* Layout desktop (inchangé) */}
          <div className="hidden lg:block w-full space-y-8">
            {/* Titre principal centré */}
            <div className="text-center">
              <ProfessionalInfoCard
                businessName={bookingClientViewModel.businessName}
                city={bookingClientViewModel.city}
                logoUrl={bookingClientViewModel.logoUrl}
              />
            </div>

            {/* Photos du salon */}
            <SalonPhotos />

            {/* Statistiques et description */}
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
              <div className="lg:col-span-2 flex flex-col">
                <ProfessionalStats 
                  reviews={bookingClientViewModel.reviews}
                  professionalId={bookingClientViewModel.professionalId}
                />
              </div>
              <div className="lg:col-span-4 flex flex-col">
                {bookingClientViewModel.description && (
                  <ProfessionalDescription description={bookingClientViewModel.description} />
                )}
              </div>
            </div>
          </div>

          {/* Layout mobile avec ordre personnalisé */}
          <div className="lg:hidden w-full flex flex-col space-y-8">
            {/* 1. ProfessionalInfoCard */}
            <div className="text-center">
              <ProfessionalInfoCard
                businessName={bookingClientViewModel.businessName}
                city={bookingClientViewModel.city}
                logoUrl={bookingClientViewModel.logoUrl}
              />
            </div>

            {/* 2. ProfessionalDescription */}
            {bookingClientViewModel.description && (
              <ProfessionalDescription description={bookingClientViewModel.description} />
            )}

            {/* 3. Grille des services */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {t('booking.selectService', 'Sélectionnez une prestation')}
                </h2>
                {Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.length > 0 && (
                  <div className="text-xs sm:text-sm text-gray-500">
                    {t('booking.servicesCount', '{{count}} services disponibles', { count: bookingClientViewModel.services.length })}
                  </div>
                )}
              </div>
              
              {bookingClientViewModel.isLoading ? (
                <div className="flex justify-center items-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.length === 0 ? (
                <div className="text-center py-16 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100">
                  <p className="text-gray-500">{t('booking.noServices', 'Aucune prestation disponible')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.map(service => (
                    <div key={service.id} className="w-full">
                      <ServiceCard
                        service={service}
                        isSelected={bookingClientViewModel.selectedService?.id === service.id}
                        onSelect={bookingClientViewModel.selectService.bind(bookingClientViewModel)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 4. ProfessionalStats */}
            <ProfessionalStats 
              reviews={bookingClientViewModel.reviews}
              professionalId={bookingClientViewModel.professionalId}
            />

            {/* 5. SalonPhotos */}
            <SalonPhotos />
          </div>
        </div>
        
        {/* Grille des services pour desktop seulement */}
        <div className="hidden lg:block max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              {t('booking.selectService', 'Sélectionnez une prestation')}
            </h2>
            {Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.length > 0 && (
              <div className="text-xs sm:text-sm text-gray-500">
                {t('booking.servicesCount', '{{count}} services disponibles', { count: bookingClientViewModel.services.length })}
              </div>
            )}
          </div>
          
          {bookingClientViewModel.isLoading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.length === 0 ? (
            <div className="text-center py-16 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100">
              <p className="text-gray-500">{t('booking.noServices', 'Aucune prestation disponible')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.map(service => (
                <div key={service.id} className="w-full">
                  <ServiceCard
                    service={service}
                    isSelected={bookingClientViewModel.selectedService?.id === service.id}
                    onSelect={bookingClientViewModel.selectService.bind(bookingClientViewModel)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      
    </div>
    <Footer />
    {/* Drawer pour les options et informations client */}
    <DrawerBooking
        isOpen={bookingClientViewModel.isDrawerOpen}
        onClose={() => {
          bookingClientViewModel.toggleDrawer(false);
          bookingClientViewModel.reset();
        }}
        position="right"
        width="90vw"
        title={t('booking.bookingDetails', 'Détails de la réservation') as string}
        className="max-w-2xl"
      >
        <div className="h-full flex flex-col">
          {/* Contenu du drawer avec défilement */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6">
            <div className="space-y-4 sm:space-y-5 md:space-y-6">

              {/* Panier de services (si plusieurs services) */}
              {bookingClientViewModel.selectedServices.length > 0 && (
                <MultiServiceCart
                  items={bookingClientViewModel.selectedServices}
                  onRemoveItem={(index) => bookingClientViewModel.removeServiceFromCart(index)}
                />
              )}

              {/* Options du service */}
              {bookingClientViewModel.selectedService && bookingClientViewModel.optionCategories.length > 0 && (
                <OptionsSelector
                  optionCategories={bookingClientViewModel.optionCategories}
                  selectedOptions={bookingClientViewModel.selectedOptions}
                  onSelectOption={bookingClientViewModel.selectOption.bind(bookingClientViewModel)}
                />
              )}

              {/* Sélection du collaborateur (si plusieurs disponibles) */}
              {bookingClientViewModel.selectedService?.collaborators && (
                <CollaboratorSelector
                  collaborators={bookingClientViewModel.selectedService.collaborators}
                  selectedCollaborator={bookingClientViewModel.selectedCollaborator}
                  onSelect={bookingClientViewModel.selectCollaborator.bind(bookingClientViewModel)}
                />
              )}

              {/* Bouton pour ajouter le service au panier */}
              {bookingClientViewModel.selectedService && bookingClientViewModel.selectedCollaborator && (
                <div className="space-y-2">
                  <Button
                    onClick={() => {
                      bookingClientViewModel.addServiceToCart();
                    }}
                    className="w-full bg-primary text-white hover:bg-primary-dark"
                    disabled={
                      // Vérifier que toutes les options obligatoires sont sélectionnées
                      bookingClientViewModel.optionCategories
                        .filter(cat => cat.isRequired)
                        .some(cat => !bookingClientViewModel.selectedOptions.some(opt => opt.categoryId === cat.id))
                    }
                  >
                    {t('booking.addToCart', 'Ajouter au panier')}
                  </Button>
                  <button
                    onClick={() => {
                      bookingClientViewModel.setSelectedService(null);
                      bookingClientViewModel.setSelectedOptions([]);
                      bookingClientViewModel.setSelectedCollaborator(null);
                      bookingClientViewModel.setOptionCategories([]);
                    }}
                    className="w-full text-sm text-gray-600 hover:text-gray-800 py-2"
                  >
                    {t('booking.cancel', 'Annuler')}
                  </button>
                </div>
              )}

              {/* Liste des services pour ajouter un autre service */}
              {bookingClientViewModel.selectedServices.length > 0 && !bookingClientViewModel.selectedService && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('booking.addAnotherService', 'Ajouter un autre service')}
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {Array.isArray(bookingClientViewModel.services) && bookingClientViewModel.services.map(service => (
                      <div
                        key={service.id}
                        onClick={() => bookingClientViewModel.selectService(service)}
                        className="bg-white rounded-lg p-3 border border-gray-200 hover:border-primary cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 text-sm mb-1">
                              {service.name}
                            </h4>
                            {service.description && (
                              <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                                {service.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {service.duration && (
                                <span>{service.duration} min</span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm font-bold text-primary ml-3">
                            {typeof service.price === 'number' ? service.price.toFixed(2) : service.price}€
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sélection de date et créneau (uniquement si aucun service n'est en cours de configuration) */}
              {bookingClientViewModel.selectedServices.length > 0 && !bookingClientViewModel.selectedService && bookingClientViewModel.requiresBooking && (
                <TimeSlotSelector
                  availableSlots={bookingClientViewModel.availableSlots}
                  selectedDate={bookingClientViewModel.selectedDate}
                  selectedSlot={bookingClientViewModel.selectedSlot}
                  onSelectDate={bookingClientViewModel.selectDate.bind(bookingClientViewModel)}
                  onSelectSlot={bookingClientViewModel.selectTimeSlot.bind(bookingClientViewModel)}
                  availableDates={[...bookingClientViewModel.availableDates]}
                />
              )}

              {/* Informations client (uniquement si aucun service n'est en cours de configuration) */}
              {!bookingClientViewModel.selectedService && (
                <>
                  <ClientForm
                    firstName={bookingClientViewModel.clientFirstName}
                    lastName={bookingClientViewModel.clientLastName}
                    email={bookingClientViewModel.clientEmail}
                    phone={bookingClientViewModel.clientPhone}
                    onChange={bookingClientViewModel.updateClientInfo.bind(bookingClientViewModel)}
                  />

                  {/* Code promo */}
                  <PromoCodeInput
                promoCode={bookingClientViewModel.promoCode}
                isValidating={bookingClientViewModel.isValidatingPromoCode}
                isValid={bookingClientViewModel.isPromoCodeValid}
                discountAmount={bookingClientViewModel.promoCodeDiscountAmount}
                validationMessage={bookingClientViewModel.promoCodeValidationMessage}
                onPromoCodeChange={bookingClientViewModel.setPromoCode.bind(bookingClientViewModel)}
                onValidate={bookingClientViewModel.validatePromoCode.bind(bookingClientViewModel)}
                onRemove={bookingClientViewModel.removePromoCode.bind(bookingClientViewModel)}
              />

                  {/* Conditions de réservation dans le drawer */}
                  {bookingClientViewModel.reservationConditions && (
                    <ReservationConditions conditions={bookingClientViewModel.reservationConditions} />
                  )}

                  {/* Message d'erreur si Stripe non configuré */}
                  {bookingClientViewModel.bookingBlockedMessage && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800">
                            {t('booking.paymentNotAvailable', 'Paiement non disponible')}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {bookingClientViewModel.bookingBlockedMessage}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Pied de page avec le total et le bouton de confirmation */}
          <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm pt-3 sm:pt-4 border-t border-gray-100 mt-3 sm:mt-4 pb-4 sm:pb-5 md:pb-6 px-3 sm:px-4">
            <div className="bg-gradient-to-r from-gray-50/80 to-white/80 backdrop-blur-sm p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                <div className="w-full sm:w-auto text-center sm:text-left">
                  {bookingClientViewModel.isPromoCodeValid && bookingClientViewModel.promoCodeDiscountAmount > 0 ? (
                    <>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <span className="text-gray-600 text-sm">{t('booking.originalPrice', 'Prix initial')}</span>
                        <span className="text-sm text-gray-500 line-through">
                          {Number(bookingClientViewModel.totalPriceBeforeDiscount).toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <span className="text-gray-600 text-sm">{t('booking.discount', 'Réduction')}</span>
                        <span className="text-sm text-green-600 font-medium">
                          -{Number(bookingClientViewModel.promoCodeDiscountAmount).toFixed(2)} €
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-primary mt-1">
                        {Number(bookingClientViewModel.totalPrice).toFixed(2)} €
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-600 text-sm">{t('booking.totalPrice', 'Prix total')}</span>
                      <p className="text-2xl font-bold text-primary">
                        {Number(bookingClientViewModel.totalPrice).toFixed(2)} €
                      </p>
                    </>
                  )}
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!bookingClientViewModel.isFormValid || !bookingClientViewModel.canConfirmBooking}
                  className="w-full sm:w-auto px-6 py-3 text-base"
                >
                  {t('booking.confirmBooking', 'Confirmer la réservation')}
                </Button>
              </div>
              <p className="text-center text-xs text-gray-500 mt-3">
                {t('booking.confirmationNote')}
              </p>
            </div>
          </div>
        </div>
      </DrawerBooking>
    </>
  );
});

export default BookingClient;